import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const pages = [{
  src: 'https://cdn.magicpatterns.com/uploads/9Lyw3m5sA5nRFa8JyvFGUz/Captura_de_pantalla_2026-09-13_a_la(s)_7.16.51_p._m..png',
  alt: 'Small group session layout for 4 athletes with four station rotation and coach position'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/aAquWebxbMVaTL7rZm5Gjg/Captura_de_pantalla_2026-09-13_a_la(s)_7.17.11_p._m..png',
  alt: 'Field-only performance session with movement prep, acceleration, change of direction, jumps and conditioning blocks'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nZus9GJY3n6Tt7RR2uLPhy/Captura_de_pantalla_2026-09-13_a_la(s)_7.17.31_p._m..png',
  alt: '12-athlete team performance session with timeline, four-station rotation and full equipment list'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/irda24S6L3VwnJErkQHzQj/Captura_de_pantalla_2026-09-13_a_la(s)_7.23.38_p._m..webp',
  alt: 'Mobility movement preparation chart covering ankle, knee, hip, thoracic spine and shoulder'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gakrqsC7jVAUEDm1pFonSh/Captura_de_pantalla_2026-09-13_a_la(s)_7.24.08_p._m..webp',
  alt: 'Max velocity coaching options including build-up sprint, flying 10, flying 20, wicket runs and straight-leg bounds'
}];
export function LookInside() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(pages.length - 1, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (slide) track.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const handleScroll = () => {
      const index = Math.round(track.scrollLeft / (track.clientWidth || 1));
      setActiveIndex(Math.max(0, Math.min(pages.length - 1, index)));
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', handleScroll);
  }, []);
  return <section className="border-y border-line bg-navy px-4 py-7 sm:py-9" data-id="element-108" data-mp="1hg091brr21oh">
      <div className="mx-auto max-w-4xl" data-id="element-109" data-mp="1hi2xvsh61orm">
        <header className="mb-4 text-center" data-id="element-110" data-mp="kqras964esql">
          <p className="text-xs font-display font-bold uppercase tracking-[0.2em] text-sport" data-id="element-111" data-mp="ks2de5ntxug4">LOOK INSIDE</p>
          <h2 className="mt-1 text-2xl font-extrabold uppercase leading-tight text-white sm:text-3xl" data-id="element-112" data-mp="pqx37rk7sj9q">
            Discover What&apos;s Inside STRENGTH READY™
          </h2>
          <p className="mx-auto mt-2 max-w-3xl text-sm leading-relaxed text-white/85" data-id="element-113" data-mp="yrboiqx6hyd8">
            Explore athlete intake forms, session layouts for small groups and full teams, field-only training plans, mobility and movement-prep charts, max velocity coaching options, station rotations, equipment lists, quality-control cues and practical coaching systems included throughout the complete package.
          </p>
        </header>

        <div className="relative" data-id="element-114" data-mp="2aj1cvobtq81p">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2" style={{
          scrollbarWidth: 'thin'
        }} tabIndex={0} role="group" aria-label="Preview pages from inside the system" data-id="element-115" data-mp="2ai9iuhxvkidj">
            {pages.map((page, index) => <figure key={page.src} className="w-full shrink-0 snap-center" data-id="element-116" data-mp="1wlmie56pra2v" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading={index === 0 ? 'eager' : 'lazy'} className="mx-auto h-auto w-full max-w-2xl rounded-lg border border-white/15 bg-white object-contain shadow-2xl" data-id="element-117" data-mp="1rkgo42xener8" />
              </figure>)}
          </div>

          <button type="button" onClick={() => scrollToIndex(activeIndex - 1)} disabled={activeIndex === 0} aria-label="Previous page" className="absolute left-1 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-navy/90 text-white transition hover:bg-electric focus:outline-none focus:ring-4 focus:ring-electric/40 disabled:opacity-35 sm:flex" data-id="element-118" data-mp="3p02n1bot9bi3">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-119" data-mp="367i4an2tsms0" />
          </button>
          <button type="button" onClick={() => scrollToIndex(activeIndex + 1)} disabled={activeIndex === pages.length - 1} aria-label="Next page" className="absolute right-1 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-navy/90 text-white transition hover:bg-electric focus:outline-none focus:ring-4 focus:ring-electric/40 disabled:opacity-35 sm:flex" data-id="element-120" data-mp="3b24l55rof7jh">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-121" data-mp="28o0gl3e9jyy5" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-center gap-2" data-id="element-122" data-mp="23qi2kyle2tja">
          {pages.map((page, index) => <button key={page.src} type="button" onClick={() => scrollToIndex(index)} aria-label={`Go to page ${index + 1}`} aria-current={activeIndex === index} className={`h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-electric ${activeIndex === index ? 'w-6 bg-sport' : 'w-2 bg-white/35'}`} data-id="element-123" data-mp="2hm3p8z45h18j" data-mpi={index} />)}
        </div>
        <p className="mt-2 text-center text-[11px] font-semibold uppercase tracking-wide text-white/60" data-id="element-124" data-mp="2wd2uq19ov9nw">
          Page {activeIndex + 1} of {pages.length} — swipe to explore
        </p>
      </div>
    </section>;
}